import React from 'react';

/**
 * ActiveFiltersBar - Displays active filters with remove buttons
 * 
 * Shows inline badges for each active filter with clear buttons.
 * Supports multiple filter types: values, text, numeric, date
 * 
 * @param {Object} props
 * @param {Array} props.activeFilters - Array of active filter objects
 * @param {string} props.searchTerm - Current global search term
 * @param {Function} props.onClearFilter - Handler to clear single filter
 * @param {Function} props.onClearSearch - Handler to clear search
 * @param {Function} props.onClearAll - Handler to clear all filters
 */
const ActiveFiltersBar = ({
  activeFilters = [],
  searchTerm = '',
  onClearFilter,
  onClearSearch,
  onClearAll,
}) => {
  const hasAnyFilters = activeFilters.length > 0 || searchTerm;

  if (!hasAnyFilters) {
    return null;
  }

  // Get badge color based on filter type
  const getBadgeColor = (type) => {
    switch (type) {
      case 'text': return '#28a745';      // Green for text filters
      case 'numeric': return '#fd7e14';   // Orange for numeric filters
      case 'date': return '#6f42c1';      // Purple for date filters
      case 'values': return '#17a2b8';    // Cyan for checkbox selection
      default: return '#6c757d';          // Gray default
    }
  };

  // Format filter description for display
  const getFilterDescription = (f) => {
    switch (f.type) {
      case 'text':
        return `${f.mode} "${f.value}"`;
      case 'numeric':
        return f.description || `${f.operator} ${f.value}`;
      case 'date':
        return f.description || `${f.operator} ${f.date}`;
      case 'values':
        return `${f.count} selected`;
      default:
        return f.count || f.value || '';
    }
  };

  return (
    <>
      <span style={{ color: '#666', fontSize: '0.8rem' }}>|</span>
      <span style={{ color: '#0056b3', fontSize: '0.8rem', fontWeight: 'bold' }}>🔍</span>
      
      {/* Search term badge */}
      {searchTerm && (
        <span style={{
          backgroundColor: '#007bff',
          color: 'white',
          padding: '2px 8px',
          borderRadius: '12px',
          fontSize: '0.75rem',
          display: 'inline-flex',
          alignItems: 'center',
          gap: '4px',
        }}>
          "{searchTerm}"
          <button
            onClick={onClearSearch}
            style={{
              background: 'none',
              border: 'none',
              color: 'white',
              cursor: 'pointer',
              padding: 0,
              fontSize: '0.85rem',
              lineHeight: 1,
            }}
          >
            ×
          </button>
        </span>
      )}
      
      {/* Filter badges */}
      {activeFilters.map((f, idx) => (
        <span
          key={idx}
          style={{
            backgroundColor: getBadgeColor(f.type),
            color: 'white',
            padding: '2px 8px',
            borderRadius: '12px',
            fontSize: '0.75rem',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px',
          }}
        >
          {f.label}: {getFilterDescription(f)}
          <button
            onClick={() => onClearFilter(f.key)}
            style={{
              background: 'none',
              border: 'none',
              color: 'white',
              cursor: 'pointer',
              padding: 0,
              fontSize: '0.85rem',
              lineHeight: 1,
            }}
          >
            ×
          </button>
        </span>
      ))}
      
      {/* Clear All button */}
      <button
        onClick={onClearAll}
        style={{
          padding: '2px 8px',
          backgroundColor: '#dc3545',
          color: 'white',
          border: 'none',
          borderRadius: '4px',
          cursor: 'pointer',
          fontSize: '0.75rem',
          fontWeight: 'bold',
        }}
      >
        Clear All
      </button>
    </>
  );
};

export default ActiveFiltersBar;
